import { useParams } from "react-router-dom";
import { useRouteContext } from "../../../context/RouteContext";
import { useState } from "react";
import useCustomEffect from "../../../hooks/useCustomEffect";
import VisitorTypePageView from "./VisitorTypePageView";
import useEntityTranslations, {
  mapResponseTranslations,
} from "../../../hooks/useEntityTranslations";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import { useAuthContext } from "../../../context/AuthContext";

function VisitorTypePage() {
  const callEvaApi = useEvaApi();
  const routeContext = useRouteContext();
  const authContext = useAuthContext();
  const { visitor_type } = useParams();
  const [submitError, setSubmitError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [flows, setFlows] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [fields, setFields] = useState([]);
  const [visitorTypeData, setVisitorTypeData] = useState({
    name: "",
    order: 0,
    earliest_time: "",
    latest_time: "",
    returning: false,
    requires_approval: false,
    identifier_fields: [],
    translations: [],
    department_id: null,
    location_id: authContext.auth.location,
    sign_in_flow_id: null,
    sign_out_flow_id: null,
    portal_flow_id: null,
    layout_id: null,
    portal_layout_id: null,
    auto_sign_out_method: "disabled",
    auto_sign_out_value: null,
  });

  const translationsController = useEntityTranslations(
    visitorTypeData.translations,
    onTranslationsChange,
  );

  /**
   * initialize the page
   */
  useCustomEffect(() => {
    if (visitor_type) {
      fetchVisitorTypeData();
    }

    fetchDepartments();
    fetchFields();
    fetchFlows();
    fetchLayouts();
  });

  /**
   * This method will fetch the fields data from the API
   */
  async function fetchFields() {
    await callEvaApi(
      new EvaApiCall("fields")
        .setLoadingGroup("visitor_type_page_fetch")
        .setOnSuccess((response) => {
          setFields(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the flows data from the API
   */
  async function fetchFlows() {
    await callEvaApi(
      new EvaApiCall("flows")
        .setLoadingGroup("visitor_type_page_fetch")
        .setOnSuccess((response) => {
          setFlows(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the layouts data from the API
   */
  async function fetchLayouts() {
    await callEvaApi(
      new EvaApiCall("layouts")
        .setLoadingGroup("visitor_type_page_fetch")
        .setOnSuccess((response) => {
          setLayouts(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the departments data from the API
   */
  async function fetchDepartments() {
    await callEvaApi(
      new EvaApiCall("departments")
        .setLoadingGroup("visitor_type_page_fetch")
        .setOnSuccess((response) => {
          setDepartments(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the visitor type data from the API
   */
  async function fetchVisitorTypeData() {
    await callEvaApi(
      new EvaApiCall(`visitor_types/${visitor_type}`)
        .setLoadingGroup("visitor_type_page_fetch")
        .setOnSuccess((response) => {
          setVisitorTypeData({
            name: response.data.data.name,
            order: response.data.data.order,
            earliest_time: response.data.data.earliest_time,
            latest_time: response.data.data.latest_time,
            returning: response.data.data.returning,
            requires_approval: response.data.data.requires_approval,
            department_id: response.data.data.department,
            location_id: response.data.data.location,
            sign_in_flow_id: response.data.data.sign_in_flow,
            sign_out_flow_id: response.data.data.sign_out_flow,
            portal_flow_id: response.data.data.portal_flow,
            layout_id: response.data.data.layout,
            portal_layout_id: response.data.data.portal_layout,
            identifier_fields: response.data.data.identifier_fields,
            auto_sign_out_method: response.data.data.auto_sign_out_method,
            auto_sign_out_value: response.data.data.auto_sign_out_value,
            translations: mapResponseTranslations(
              response.data.data.translations,
              (translation) => ({
                label: translation.label,
              }),
            ),
          });
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will handle the translations change
   * @param {array} value
   */
  function onTranslationsChange(value) {
    setVisitorTypeData((current) => ({
      ...current,
      translations: value,
    }));
  }

  /**
   * This method will update the location id on input change
   * @param {any} value
   */
  function onLocationIdChange(value) {
    setVisitorTypeData({ ...visitorTypeData, location_id: value });
  }

  /**
   * This method will update the department on input change
   * @param {any} value
   */
  function onDepartmentChange(value) {
    setVisitorTypeData({ ...visitorTypeData, department_id: value });
  }

  /**
   * This method will update the sign in flow on input change
   * @param {any} value
   */
  function onSignInFlowChange(value) {
    setVisitorTypeData({ ...visitorTypeData, sign_in_flow_id: value });
  }

  /**
   * This method will update the sign out flow on input change
   * @param {any} value
   */
  function onSignOutFlowChange(value) {
    setVisitorTypeData({ ...visitorTypeData, sign_out_flow_id: value });
  }

  /**
   * This method will update the portal flow on input change
   * @param {any} value
   */
  function onPortalFlowChange(value) {
    setVisitorTypeData({ ...visitorTypeData, portal_flow_id: value });
  }

  /**
   * This method will update the layout on input change
   * @param {any} value
   */
  function onLayoutChange(value) {
    setVisitorTypeData({ ...visitorTypeData, layout_id: value });
  }

  /**
   * This method will update the portal layout on input change
   * @param {any} value
   */
  function onPortalLayoutChange(value) {
    setVisitorTypeData({ ...visitorTypeData, portal_layout_id: value });
  }

  /**
   * This method will update the name on input change
   * @param {event} e
   */
  function onNameChange(e) {
    setVisitorTypeData({ ...visitorTypeData, name: e.target.value });
  }

  /**
   * This method will handle the order change
   * @param {event} e
   */
  function onOrderChange(e) {
    setVisitorTypeData({ ...visitorTypeData, order: e.target.value });
  }

  /**
   * This method will handle the earliest time change
   * @param {event} e
   */
  function onEarliestTimeChange(e) {
    setVisitorTypeData({ ...visitorTypeData, earliest_time: e.target.value });
  }

  /**
   * This method will handle the latest time change
   * @param {event} e
   */
  function onLatestTimeChange(e) {
    setVisitorTypeData({ ...visitorTypeData, latest_time: e.target.value });
  }

  /**
   * This method will handle the returning change
   * @param {any} value
   */
  function onReturningChange(value) {
    setVisitorTypeData({ ...visitorTypeData, returning: value });
  }

  /**
   * This method will handle the requires approval change
   * @param {any} value
   */
  function onRequiresApprovalChange(value) {
    setVisitorTypeData({ ...visitorTypeData, requires_approval: value });
  }

  /**
   * This method will update the identifier fields on input change
   * @param {array} values
   */
  function onIdentifierFieldsChange(values) {
    setVisitorTypeData({
      ...visitorTypeData,
      identifier_fields: values,
    });
  }

  /**
   * This method will handle the auto sign out method change
   * @param {string} value
   */
  function onAutoSignOutMethodChange(value) {
    let autoSignOutValue = null;
    switch (value) {
      case "after":
        autoSignOutValue = 8;
        break;
      case "at":
        autoSignOutValue = "23:59";
        break;
      default:
        autoSignOutValue = null;
        break;
    }

    setVisitorTypeData((prev) => ({
      ...prev,
      auto_sign_out_method: value,
      auto_sign_out_value: autoSignOutValue,
    }));
  }

  /**
   * This method will handle the auto sign out value change
   * @param {event} e
   */
  function onAutoSignOutValueChange(e) {
    const value = e.target.value;

    setVisitorTypeData((prev) => ({
      ...prev,
      auto_sign_out_value: value,
    }));
  }

  /**
   * This method will handle the cancel functionality
   */
  function onCancel() {
    routeContext.back();
  }

  /**
   * This method will handle the submit functionality
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(
        visitor_type ? `visitor_types/${visitor_type}` : `visitor_types`,
      )
        .setLoadingGroup("visitor_type_page_submit")
        .setMethod(visitor_type ? "PUT" : "POST")
        .setData(visitorTypeData)
        .setErrorState(setSubmitError)
        .setRedirectOnSuccess(true),
    );
  }

  return (
    <VisitorTypePageView
      visitorTypeData={visitorTypeData}
      visitor_type={visitor_type}
      departments={departments}
      fields={fields}
      flows={flows}
      layouts={layouts}
      onDepartmentChange={onDepartmentChange}
      onLocationIdChange={onLocationIdChange}
      onSignInFlowChange={onSignInFlowChange}
      onSignOutFlowChange={onSignOutFlowChange}
      onPortalFlowChange={onPortalFlowChange}
      onLayoutChange={onLayoutChange}
      onPortalLayoutChange={onPortalLayoutChange}
      onNameChange={onNameChange}
      onOrderChange={onOrderChange}
      onEarliestTimeChange={onEarliestTimeChange}
      onLatestTimeChange={onLatestTimeChange}
      onReturningChange={onReturningChange}
      onRequiresApprovalChange={onRequiresApprovalChange}
      onIdentifierFieldsChange={onIdentifierFieldsChange}
      onAutoSignOutMethodChange={onAutoSignOutMethodChange}
      onAutoSignOutValueChange={onAutoSignOutValueChange}
      translationsController={translationsController}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default VisitorTypePage;
